/**
 * WHERE A CONFIRMATION LINK LANDS -- THE PORTAL'S COPY.
 *
 * The platform carries the same screen at the same path. They are separate
 * origins served by separate Vercel projects, so each needs its own, and both
 * origins must be in Supabase's Redirect URLs allow-list.
 *
 * The founder, 14 September:
 *
 *   "Why is it that when I send myself a 'change your email address'
 *    confirmation email, and I click on the link, that it just takes me to the
 *    Orchid marketing site? I've been trying to make
 *    eddie@orchidholdings.co.za (albeit an alias) my email address for the
 *    longest time."
 *
 * Three separate faults, and this screen is the answer to the second.
 *
 *   1. `requestEmailChange` called `supabase.auth.updateUser({ email })` with no
 *      `emailRedirectTo`, so Supabase built the link with `redirect_to`
 *      defaulting to the project's SITE URL — the marketing root.
 *   2. There was nowhere for it to land: this Portal had no /auth/confirm route
 *      at all, and its catch-all sends everything unknown to '/'.
 *   3. Secure email change is ON, so Supabase sends a confirmation to BOTH the
 *      old and the new address and BOTH must be clicked. The founder: "I enter
 *      the eddie@ email ... and the confirmation email with the link DOES
 *      arrive in the info@ inbox" -- that is the one addressed to his CURRENT
 *      address, working as designed, with nothing saying a second existed.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SCREEN DOES, AND WHAT IT DELIBERATELY DOES NOT
 *
 * It does NOT perform the change. By the time a browser reaches this route
 * Supabase has already verified the token server-side; the link is spent. This
 * screen's whole job is to read the session back and SAY WHAT IS NOW TRUE,
 * which is the thing the marketing page could not do.
 *
 * And it distinguishes the two states that look identical from the outside:
 * one link clicked of two, and both clicked. `auth.users.email` only becomes
 * the new address once both are in, so the session is the only honest source
 * for which address signs in now — this reads it rather than telling somebody
 * what they probably did.
 */

import { useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from './lib/supabaseClient';

const WRAP = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: '#F7F5F0',
};

const CARD = {
  maxWidth: 460,
  width: '100%',
  background: '#FFFFFF',
  borderRadius: 14,
  padding: 28,
  border: '1px solid #E4E0D8',
};

const TITLE = {
  font: '700 19px/1.3 Georgia, "Times New Roman", serif',
  color: '#10241C',
  margin: '0 0 10px',
};

const BODY = {
  font: '400 14px/1.7 system-ui, -apple-system, "Segoe UI", sans-serif',
  color: '#4A554F',
  margin: '0 0 8px',
};

const BUTTON = {
  marginTop: 18,
  width: '100%',
  padding: '12px 16px',
  borderRadius: 999,
  border: 'none',
  background: '#062F20',
  color: '#FFFFFF',
  font: '600 14px system-ui, -apple-system, "Segoe UI", sans-serif',
  cursor: 'pointer',
};

/**
 * NO useNavigate: this renders from App.jsx BEFORE the BrowserRouter is
 * mounted, because somebody arriving on a confirmation link otherwise looks
 * like an ordinary signed-in operator and every gate below carries them into
 * the Portal without ever saying what the link did.
 */
export default function AuthConfirm() {
  const [state, setState] = useState({ reading: true, email: null, failure: null });

  useEffect(() => {
    let alive = true;

    const read = async () => {
      if (!isSupabaseConfigured()) {
        if (alive) {
          setState({
            reading: false,
            email: null,
            failure: 'This site was built without its Supabase credentials, so it cannot '
                   + 'read your account.',
          });
        }
        return;
      }

      // THE SESSION, NOT THE URL. The token in the link is spent by the time a
      // browser gets here, and reading the address out of the query string
      // would say what was REQUESTED rather than what is now true -- which is
      // exactly the distinction that matters while one of two links is
      // outstanding.
      const { data, error } = await supabase.auth.getUser();
      if (!alive) return;

      if (error || !data || !data.user) {
        setState({
          reading: false,
          email: null,
          failure: null,
        });
        return;
      }
      setState({ reading: false, email: data.user.email || null, failure: null });
    };

    read();
    return () => { alive = false; };
  }, []);

  const go = () => { window.location.assign('/'); };

  return (
    <div style={WRAP}>
      <div style={CARD}>
        {state.reading && (
          <>
            <h1 style={TITLE}>Checking that link…</h1>
            <p style={BODY}>One moment.</p>
          </>
        )}

        {!state.reading && state.failure && (
          <>
            <h1 style={TITLE}>Orchid could not read your account</h1>
            <p style={BODY}>{state.failure}</p>
          </>
        )}

        {/* SIGNED OUT ON THIS DEVICE, which is the ordinary case: confirmation
            links are opened from a mail client, often on the other phone. The
            link still did its work -- it was verified before this page loaded --
            so this says so rather than implying it failed. */}
        {!state.reading && !state.failure && !state.email && (
          <>
            <h1 style={TITLE}>That link has been used</h1>
            <p style={BODY}>
              Thank you — Orchid has recorded it. You are not signed in on this device,
              so there is nothing more to see here.
            </p>
            <p style={BODY}>
              <strong>If you were changing your email address</strong>, Orchid sends a
              confirmation to both your old address and the new one, and the change only
              takes effect once <strong>both</strong> have been clicked. If you have only
              opened one, look for the other before signing in.
            </p>
            <button type="button" style={BUTTON} onClick={go}>Go to sign in</button>
          </>
        )}

        {!state.reading && !state.failure && state.email && (
          <>
            <h1 style={TITLE}>That link has been used</h1>
            <p style={BODY}>
              The address Orchid has for you right now is <strong>{state.email}</strong>,
              and it is the one you sign in with.
            </p>
            {/* SAID EVEN WHEN IT LOOKS DONE. Half a secure email change reads
                exactly like none of it: the old address still signs in, and
                nothing anywhere says a second link is outstanding. */}
            <p style={BODY}>
              If you were changing it and this is still the old address, the second
              confirmation has not been opened yet — Orchid sends one to each address and
              needs both.
            </p>
            <button type="button" style={BUTTON} onClick={go}>Continue</button>
          </>
        )}
      </div>
    </div>
  );
}
